/**
 * Ask for a workspace (`/signup?token=...`), DEC-202, DEC-205, UI_SDD §R.15.
 *
 * Reached from the emailed "Request your TDIA workspace" link, or from a Google sign-in
 * for an address that has no workspace yet. The link is the proof of the address; this
 * page names the workspace and sends the request. A Super Admin approves it, and the
 * person gets an email with a sign-in link (DEC-205). Nothing is created here. Opening the URL
 * spends nothing: the token is spent by the POST the button makes, for the same reason
 * LinkSignInPage never redeems on load (mail scanners prefetch links).
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  signupComplete,
  signupFailureMessage,
  signupFailureReason,
  signupPreview,
  type SignupFailureReason,
  type SignupPreview,
} from '../api/auth'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/shared/Button'
import { Spinner } from '../components/shared/Spinner'
import { PageTitle } from '../components/shared/PageTitle'

const FAILURES: Record<SignupFailureReason, { heading: string; body: string; canRetry: boolean }> = {
  not_found: {
    heading: 'This link is not valid',
    body: 'The link may have been copied incompletely. Try opening it from your email again.',
    canRetry: true,
  },
  expired: {
    heading: 'This link has expired',
    body: 'Links to create a workspace are valid for 24 hours. Request a new one from the sign in page.',
    canRetry: true,
  },
  already_used: {
    heading: 'This link has already been used',
    body: 'Your workspace already exists. Sign in to reach it.',
    canRetry: true,
  },
  already_member: {
    heading: 'You already have a workspace',
    body: 'This address already belongs to a workspace. Sign in to reach it.',
    canRetry: true,
  },
  invalid_name: {
    heading: 'Check the workspace name',
    body: 'Give your workspace a name under 80 characters.',
    canRetry: false,
  },
  rate_limited: {
    heading: 'Too many new workspaces from here',
    body: 'Too many workspaces were requested from this network in the last hour. Try again later.',
    canRetry: true,
  },
  declined: {
    heading: 'Your request was not approved',
    body: 'Your request for a workspace was not approved. You can ask again 30 days after it was declined.',
    canRetry: false,
  },
}

/** The one place this sentence is written on the page (DEC-205). */
const WAITING = 'Thanks. Your request is waiting for approval. We will email you when it is ready.'


type State =
  | { phase: 'loading' }
  | { phase: 'ready'; preview: SignupPreview }
  | { phase: 'failed'; reason: SignupFailureReason; message?: string | null }
  | { phase: 'waiting'; email: string; message: string }

export default function SignupPage() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const [state, setState] = useState<State>({ phase: 'loading' })
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loaded = useRef(false)

  useEffect(() => {
    if (loaded.current) return
    loaded.current = true
    if (!token) {
      setState({ phase: 'failed', reason: 'not_found' })
      return
    }
    void (async () => {
      try {
        const preview = await signupPreview(token)
        if (preview.request_status === 'pending') {
          setState({ phase: 'waiting', email: preview.email, message: preview.request_message ?? WAITING })
          return
        }
        if (preview.request_status === 'declined') {
          setState({ phase: 'failed', reason: 'declined', message: preview.request_message })
          return
        }
        setName(preview.suggested_name)
        setState({ phase: 'ready', preview })
      } catch (err) {
        setState({ phase: 'failed', reason: signupFailureReason(err) })
      }
    })()
  }, [token])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!token || !name.trim()) return
    setSaving(true)
    setError(null)
    try {
      const result = await signupComplete(token, name.trim())
      if (result.status === 'approved') {
        // A Super Admin's own first sign-up is approved at once and signed in (DEC-205).
        await refresh()
        navigate('/connect', { replace: true })
        return
      }
      setState({ phase: 'waiting', email: result.email, message: result.message || WAITING })
    } catch (err) {
      const reason = signupFailureReason(err)
      if (reason === 'invalid_name') {
        setError(FAILURES.invalid_name.body)
        setSaving(false)
        return
      }
      setState({ phase: 'failed', reason, message: signupFailureMessage(err) })
    }
  }

  return (
    <div className="min-h-screen bg-ground flex flex-col">
      <header className="h-12 border-b border-hairline bg-panel flex items-center px-6">
        <span className="text-[15px] font-semibold text-ink tracking-tight">TDIA</span>
      </header>

      <main className="flex-1 flex items-start justify-center pt-24 px-4">
        <div className="w-full max-w-sm">
          {state.phase === 'loading' && (
            <div className="flex items-center gap-3">
              <Spinner />
              <span className="text-[14px] text-muted">Checking your link...</span>
            </div>
          )}

          {state.phase === 'ready' && (
            <>
              <div className="mb-8">
                <PageTitle eyebrow="Tech Debt Intelligence" size="compact">
                  Request a workspace
                </PageTitle>
                <p className="text-[14px] text-muted mt-1">
                  For <span className="text-ink">{state.preview.email}</span>. Athenaworks reviews each
                  request. Once it is approved you will be the workspace admin and can invite your team.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
                <div>
                  <label htmlFor="workspace-name" className="block text-[13px] font-medium text-body mb-1.5">
                    Workspace name
                  </label>
                  <input
                    id="workspace-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    autoFocus
                    disabled={saving}
                    className="w-full border border-control rounded-md px-3 py-2 text-[14px] text-ink placeholder-faint focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
                  />
                </div>

                {error && <p className="text-[13px] text-critical">{error}</p>}

                <Button
                  type="submit"
                  disabled={name.trim().length === 0}
                  loading={saving}
                  className="w-full justify-center py-2.5"
                >
                  Send request
                </Button>
              </form>

              <p className="text-[12px] text-faint mt-6 leading-relaxed">
                AI analysis starts once you add an Anthropic key in Settings. Until then, scans run
                the checks that need no AI.
              </p>
            </>
          )}

          {state.phase === 'waiting' && (
            <div data-testid="signup-waiting">
              <PageTitle eyebrow="Tech Debt Intelligence" size="compact">
                Request sent
              </PageTitle>
              <p className="text-[14px] text-body mt-2 leading-relaxed">{state.message}</p>
              <p className="text-[13px] text-muted mt-2 leading-relaxed">
                The email will go to <span className="text-ink">{state.email}</span>.
              </p>
            </div>
          )}

          {state.phase === 'failed' && (
            <div>
              <PageTitle size="compact">{FAILURES[state.reason].heading}</PageTitle>
              <p className="text-[14px] text-muted mt-2 leading-relaxed">
                {(state.reason === 'declined' && state.message) || FAILURES[state.reason].body}
              </p>
              {FAILURES[state.reason].canRetry && (
                <Button className="mt-6 justify-center py-2.5" onClick={() => navigate('/login', { replace: true })}>
                  Go to sign in
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
